import React, { useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getCachedRawAuditionList } from '../../services/auditionService';
import styles from './AuditionsPage.module.css';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import AuditionsPageHeader from './AuditionsPageHeader';
import AuditionsFilterBar from './AuditionsFilterBar';
import AuditionsGrid from './AuditionsGrid';
import GroupedPagination from '../../components/common/Pagination/GroupedPagination';
import useAuditions from '../../hooks/useAuditions';
import { useRotatingAd } from '../../shared/hooks/useAds';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { useSavedItems } from '../../contexts/SavedItemsContext';
import RoleOnlyModal from '../../shared/components/RoleOnlyModal';
import BoardPickerModal, { SavedConfirmModal } from '../../shared/components/BoardPickerModal';
import { C, t } from '../Talent/Dashboard/components/dashboardConstants';

/**
 * @page AuditionsPage
 * @description 공고 리스트 페이지 (/auditions)
 *
 * 상태 흐름:
 *   loading  → 스켈레톤 (현재: 로딩 텍스트)
 *   error    → 에러 메시지 + 재시도 버튼
 *   empty    → 결과 없음 안내
 *   success  → AuditionsGrid + GroupedPagination
 *
 * URL 쿼리 파라미터와 필터 상태 동기화
 */
function AuditionsPage() {
  const { isLoggedIn, role, isTalent, isProducer, switchRole } = useAuth();
  const { castingMap, toggleSave, boardPickerState, savedConfirmState, selectBoard, createAndSelectBoard, closeBoardPicker, closeSavedConfirm } = useSavedItems();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('card');

  // ── URL 쿼리 → 필터 상태 ──────────────────────────────────────────────────
  const search = searchParams.get('search') ?? '';
  const category = searchParams.get('category') ?? '';
  const location = searchParams.get('location') ?? '';
  const status = searchParams.get('status') ?? '';
  const sort = searchParams.get('sort') ?? 'latest';
  const page = parseInt(searchParams.get('page') ?? '1', 10);

  // ── 읽은 공고 상태 ────────────────────────────────────────────────────────
  const [readIds, setReadIds] = useState(new Set());
  const [modalState, setModalState] = useState(null);

  const getFilteredCount = useCallback(async (cats, locs) => {
    // fetchAuditions()와 동일한 캐시를 공유해서 중복으로 전체 목록을 다시 받지 않는다.
    let rawList;
    try {
      rawList = await getCachedRawAuditionList();
    } catch {
      return 0;
    }
    const HIDDEN = new Set(['DRAFT']);
    let list = rawList
      .filter(item => !HIDDEN.has(item.status));

    if (search) {
      const kw = search.toLowerCase();
      list = list.filter(item =>
        (item.title || '').toLowerCase().includes(kw) ||
        (item.companyName || '').toLowerCase().includes(kw)
      );
    }
    if (cats && cats.length > 0) {
      list = list.filter(item => cats.includes(item.projectType || item.category || ''));
    }
    if (locs && locs.length > 0) {
      list = list.filter(item => {
        const province = (item.regionProvince || '').trim();
        const city = (item.regionCity || '').trim();
        const normalizedCity = city === province ? '' : city;
        return locs.some(selected => {
          const norm = selected.trim();
          if (!norm) return false;
          if (!norm.includes(' ')) return province === norm || normalizedCity === norm;
          const [sp, sc] = norm.split(' ');
          return province === sp && normalizedCity === sc;
        });
      });
    }
    return list.length;
  }, [search]);

  // ── 필터 변경 → URL 쿼리 업데이트 ────────────────────────────────────────
  const updateParams = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      });
      // 필터 변경 시 page 초기화
      if (!('page' in updates)) {
        next.set('page', '1');
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const handleSearchChange = useCallback(
    (value) => updateParams({ search: value }),
    [updateParams]
  );
  const handleApplyFilter = useCallback(
    (categoryValue, locationValue, sortValue) => updateParams({ category: categoryValue, location: locationValue, sort: sortValue || 'latest' }),
    [updateParams]
  );
  const handlePageChange = useCallback(
    (nextPage) => updateParams({ page: String(nextPage) }),
    [updateParams]
  );
  const handleFilterReset = useCallback(
    () => updateParams({ category: '', location: '', sort: '' }),
    [updateParams]
  );

  // ── 저장 토글 ─────────────────────────────────────────────────────────────
  const handleSaveToggle = useCallback(async (id) => {
    if (role !== 'TALENT') {
      setModalState({
        title: '배우 계정 전용 기능',
        message: isLoggedIn ? '공고 저장은 배우 계정에서만 이용할 수 있습니다.' : '배우로 로그인해서 이용해주세요.',
        roleType: 'TALENT',
      });
      return;
    }
    toggleSave(id, 'CASTING_POST');
  }, [isLoggedIn, role, toggleSave]);

  // ── 카드 클릭 → 상세 페이지 (읽음 처리) ─────────────────────────────────
  const handleCardClick = useCallback(
    (id) => {
      setReadIds((prev) => { const next = new Set(prev); next.add(id); return next; });
      navigate(`/castings/${id}`, { state: { backLabel: '공고 찾기로 돌아가기' } });
    },
    [navigate]
  );

  // ── 데이터 조회 ──────────────────────────────────────────────────────────
  // 1페이지 카드 뷰에 광고가 끼면 "광고 포함 9개"가 되도록 1페이지만 8개를 받아온다.
  const { ad: auditionsPageAd } = useRotatingAd('auditions-grid');
  const showAdOnFirstPage = viewMode === 'card' && Boolean(auditionsPageAd);
  const { data, activeTotal, totalPages, isLoading, isError, isEmpty, retry } = useAuditions({
    search,
    category,
    location,
    status,
    sort,
    page,
    limit: 9,
    firstPageLimit: showAdOnFirstPage ? 8 : 9,
  });

  return (
    <PageLayout>
      {modalState && (
        <RoleOnlyModal
          title={modalState.title}
          message={modalState.message}
          onClose={() => setModalState(null)}
          hasBothRoles={isLoggedIn && isTalent && isProducer}
          onSwitch={modalState.roleType === 'TALENT'
            ? () => { setModalState(null); switchRole('TALENT'); window.location.href = '/talent/dashboard'; }
            : () => { setModalState(null); switchRole('PRODUCER'); window.location.href = '/producer/dashboard'; }
          }
          switchLabel={modalState.roleType === 'TALENT' ? '배우로 계정 전환하기' : '제작사로 계정 전환하기'}
          onboardingPath={modalState.roleType === 'TALENT'
            ? (isLoggedIn && !isTalent ? '/onboarding/talent' : undefined)
            : (isLoggedIn && !isProducer ? '/onboarding/producer' : undefined)
          }
          onboardingLabel={modalState.roleType === 'TALENT' ? '배우 계정 만들러 가기' : '제작사 계정 만들러 가기'}
        />
      )}
      {boardPickerState && <BoardPickerModal boards={boardPickerState.options} targetType={boardPickerState.targetType} onClose={closeBoardPicker} onSelect={selectBoard} onCreateAndSelect={createAndSelectBoard} />}
      {savedConfirmState && <SavedConfirmModal boardName={savedConfirmState.boardName} onClose={closeSavedConfirm} onNavigateToArchive={() => {
          try { localStorage.setItem('talent-archive-open-board', String(savedConfirmState.boardId)); } catch {}
          closeSavedConfirm();
          navigate('/talent/archive');
        }} />}
      <div className={styles.container}>
        {/* 페이지 헤더 */}
        <AuditionsPageHeader
          onCreateClick={() => {
            if (role !== 'PRODUCER') {
              setModalState({
                title: '제작사 계정 전용 기능',
                message: isLoggedIn ? '공고 작성은 제작사 계정에서만 이용할 수 있습니다.' : '제작사로 로그인해서 이용해주세요.',
                roleType: 'PRODUCER',
              });
              return;
            }
            navigate('/producer/castings/new');
          }}
        />

        {/* 필터 바 */}
        <AuditionsFilterBar
          search={search}
          onSearchChange={handleSearchChange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          category={category}
          onApplyFilter={handleApplyFilter}
          location={location}
          sort={sort}
          onReset={handleFilterReset}
          getFilteredCount={getFilteredCount}
        />

        {/* 결과 메타 */}
        {!isLoading && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <span style={t('15px', 400, C.sub)}>
              총 <strong style={{ color: C.bk }}>{activeTotal.toLocaleString()}</strong>개의 진행 중인 공고
            </span>
          </div>
        )}

        {/* 콘텐츠 영역 */}
        <div className={styles.content}>
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '80px 0', display: 'flex', justifyContent: 'center' }}><LoadingSpinner size="lg" /></div>
          )}

          {isError && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div style={t('16px', 600, C.bk)}>데이터를 불러오지 못했습니다.</div>
              <button onClick={retry} style={{ marginTop: '12px', padding: '8px 20px', borderRadius: '10px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('14px', 500, C.bk) }}>
                다시 시도
              </button>
            </div>
          )}

          {isEmpty && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <div style={t('18px', 600, C.bk)}>검색 결과가 없습니다</div>
              <div style={{ ...t('14px', 400, C.sub), marginTop: '8px' }}>다른 검색어나 필터를 사용해 보세요.</div>
            </div>
          )}

          {!isLoading && !isError && !isEmpty && (
            <>
              <AuditionsGrid
                items={data}
                savedIds={new Set(Object.keys(castingMap))}
                savedColorMap={Object.fromEntries(Object.entries(castingMap).map(([id, entry]) => [id, entry?.boardColor || null]))}
                readIds={readIds}
                onCardClick={handleCardClick}
                onSaveToggle={handleSaveToggle}
                viewMode={viewMode}
                page={page}
              />
              <div className={styles.pagination}>
                <GroupedPagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}

export default AuditionsPage;
